import { PubSubConfig, PubSubProvider } from '@/types/PubSubProvider';
import { createSelector } from '@reduxjs/toolkit';
import { GlobalState } from '../rootReducer';
import { LOAD_LIVE_CATALOG_STATUS_SUCCESS } from './actions';

export interface State {
    algoliaApiKey: string;
    algoliaAppId: string;
    baseUrl: string;
    buildNumber: string;
    classicBaseUrl: string;
    curatorImgUrl: string;
    deployment: string;
    globalNotificationClientType: string;
    googleApiKey: string;
    helpScout: object;
    logRocketAppId: string;
    messagingProvider: PubSubProvider;
    messagingProviderKeyConfigs: {
        ably: PubSubConfig;
        pubnub: PubSubConfig;
    };
    partnersBaseUrl: string;
    pubNubNotificationsSubscribeKey: string;
    pubNubSubscribeKey: string;
    pushSubscriptionPublicKey: string;
    quadpayMerchantKey: string;
    referralCandy: object;
    sailthruCustomerId: string;
    segmentWriteKey: string;
    stripePublishKey: string;
    useSignifyd: boolean;
    wwwBaseUrl: string;
}

/* reducer */
export const DEFAULT_STATE: State = {
    algoliaApiKey: '',
    algoliaAppId: '',
    baseUrl: '',
    buildNumber: '',
    classicBaseUrl: '',
    curatorImgUrl: '',
    deployment: '',
    globalNotificationClientType: '',
    googleApiKey: '',
    helpScout: {},
    logRocketAppId: '',
    messagingProvider: null,
    messagingProviderKeyConfigs: {
        ably: {
            publishKey: '',
            subscribeKey: '',
        },
        pubnub: {
            publishKey: '',
            subscribeKey: '',
        },
    },
    partnersBaseUrl: '',
    pubNubNotificationsSubscribeKey: '',
    pubNubSubscribeKey: '',
    pushSubscriptionPublicKey: '',
    quadpayMerchantKey: '',
    referralCandy: {},
    sailthruCustomerId: '',
    segmentWriteKey: '',
    stripePublishKey: '',
    useSignifyd: false,
    wwwBaseUrl: '',
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_LIVE_CATALOG_STATUS_SUCCESS:
            if (action.payload.status === 'notLoaded') {
                return state;
            }

            // Update pub/sub keys from auction engine state
            return {
                ...state,
                messagingProvider: action.payload?.bidder?.messagingProvider || state.messagingProvider,
                messagingProviderKeyConfigs: action.payload?.bidder?.subscriptions || state.messagingProviderKeyConfigs,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.config;

export const getAlgoliaApiKey = createSelector(stateSelector, (state) => state.algoliaApiKey);
export const getAlgoliaAppId = createSelector(stateSelector, (state) => state.algoliaAppId);
export const getBaseUrl = createSelector(stateSelector, (state) => state.baseUrl);
export const getBuildNumber: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.buildNumber
);
export const getClassicBaseUrl: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.classicBaseUrl
);
export const getCuratorBaseUrl: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.curatorImgUrl
);
export const getDeployment: (state: GlobalState) => string = createSelector(stateSelector, (state) => state.deployment);
export const getGlobalNotificationClientType: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.globalNotificationClientType
);
export const getGoogleApiKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.googleApiKey
);
export const getHelpScoutInformation: (state: GlobalState) => any = createSelector(
    stateSelector,
    (state) => state.helpScout
);
export const getLogRocketAppId = createSelector(stateSelector, (state: State) => state.logRocketAppId);
export const getPartnersBaseUrl = createSelector(stateSelector, (state: State) => state.partnersBaseUrl);
export const getPubnubNotificationsSubscribeKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.pubNubNotificationsSubscribeKey
);
export const getPubnubSubscribeKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.pubNubSubscribeKey
);
export const getPushSubscriptionPublicKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.pushSubscriptionPublicKey
);
export const getQuadpayMerchantKey: (state: GlobalState) => any = createSelector(
    stateSelector,
    (state) => state.quadpayMerchantKey
);
export const getReferralCandy: (state: GlobalState) => any = createSelector(
    stateSelector,
    (state) => state.referralCandy
);
export const getSailthruCustomerId: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.sailthruCustomerId
);
export const getSegmentWriteKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.segmentWriteKey
);
export const getStripePublishKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.stripePublishKey
);
export const getUseSignifyd: (state: GlobalState) => boolean = createSelector(
    stateSelector,
    (state) => state.useSignifyd
);
export const getWWWBaseUrl: (state: GlobalState) => string = createSelector(stateSelector, (state) => state.wwwBaseUrl);

export const getMessagingProviderKeyConfigs = createSelector(
    stateSelector,
    (state: State) => state.messagingProviderKeyConfigs
);

export const getPubSubMessagingProvider = createSelector(stateSelector, (state: State) => state.messagingProvider);

/**
 * Returns the publishKey and subscribeKey for the current messagingProvider
 */
export const getPubSubMessagingProviderKeys = createSelector(
    [getMessagingProviderKeyConfigs, getPubSubMessagingProvider],
    (messagingProviderKeyConfigs, messagingProvider): PubSubConfig => {
        return (
            messagingProviderKeyConfigs[messagingProvider] || {
                publishKey: '',
                subscribeKey: '',
            }
        );
    }
);

/**
 * returns true if there is a valid subscribe key for the current pub/sub provider
 * useful for knowing if we can use the pub/sub rest apis
 */
export const hasPubSubConfig = createSelector(
    [getPubSubMessagingProviderKeys],
    (messagingProviderKeyConfig): boolean => {
        return Boolean(messagingProviderKeyConfig.subscribeKey);
    }
);
